export { ReactQuerySetup } from "./ReactQuerySetup";

import { paths } from "@classdojo/ts-api-types";
import { useCallback, useMemo } from "react";
import {
  InfiniteData,
  QueryClient,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseMutationResult,
} from "react-query";
import { getApiClient } from "src/utils/axios";

type FetcherConfigs = {
  [fetcherName: string]: {
    invalidator?: (params: any, mutation: any) => boolean;
    reducer?: (params: any, data: any, mutation: any) => any;
  };
};
type OperationConfigs = {
  [operationName: string]: boolean;
};
export type CombinedConfigs = {
  fetchers: FetcherConfigs;
  operations: OperationConfigs;
};

//
// an ugly dance to allow separate unit tests to redeclare fetchers and operations without
// failing the duplication assertion.
//
const actualCombinedConfigs: CombinedConfigs = { fetchers: {}, operations: {} };
const combinedConfigsRef = { current: actualCombinedConfigs };

export function testOnly_stubCombinedConfigs() {
  combinedConfigsRef.current = { fetchers: {}, operations: {} };
}

export function testOnly_restoreCombinedConfigs() {
  combinedConfigsRef.current = actualCombinedConfigs;
}

const getRelativePath = (urlStr: string) => {
  const url = new URL(urlStr);
  return url.pathname + url.search;
};

interface RouteWithJsonResponse {
  responses: {
    200: {
      content: {
        "application/json": any;
      };
    };
  };
}
interface RouteWithCollectionJsonResponse {
  responses: {
    200: {
      content: {
        "application/json": {
          _items: any[];
        };
      };
    };
  };
}
interface RouteWithQueryParams {
  parameters: {
    query: any;
  };
}
interface RouteWithPathParams {
  parameters: {
    path: any;
  };
}

type ResponseFromRouteDefinition<RouteDefinition> =
  RouteDefinition extends RouteWithJsonResponse
    ? RouteDefinition["responses"][200]["content"]["application/json"]
    : never;

type PathParamsFromRouteDefinition<RouteDefinition> =
  RouteDefinition extends RouteWithPathParams
    ? RouteDefinition["parameters"]["path"]
    : never;

export type APIResponse<
  Path extends keyof paths,
  Method extends keyof paths[Path]
> = Method extends keyof paths[Path]
  ? ResponseFromRouteDefinition<paths[Path][Method]>
  : never;

export type APIPathParams<
  Path extends keyof paths,
  Method extends keyof paths[Path]
> = Method extends keyof paths[Path]
  ? PathParamsFromRouteDefinition<paths[Path][Method]>
  : never;

const getNextPageParam = (lastPage: {
  _links?: { next?: { href: string } };
}) => {
  // it is ClassDojo convention to put the next page link at _links.next.href
  return lastPage._links?.next?.href;
};

export const NOOP = "fetchers/NOOP";

export function makeMemberFetcher<
  Path extends keyof paths,
  Method extends "get",
  RouteDefinition extends Method extends keyof paths[Path]
    ? paths[Path][Method]
    : never,
  PathParams extends RouteDefinition extends RouteWithPathParams
    ? RouteDefinition["parameters"]["path"]
    : never,
  QueryParams extends RouteDefinition extends RouteWithQueryParams
    ? RouteDefinition["parameters"]["query"]
    : never,
  Response extends RouteDefinition extends RouteWithJsonResponse
    ? RouteDefinition["responses"][200]["content"]["application/json"]
    : never
>({
  pathPattern,
  shouldInvalidate,
  memberReducer,
  staleTime,
}: {
  pathPattern: Path;
  shouldInvalidate?: (
    fetcherParams: { path: PathParams; query: QueryParams },
    mutation: MutationAction
  ) => boolean;
  memberReducer?: (
    fetcherParams: { path: PathParams; query: QueryParams },
    data: Response,
    mutation: MutationAction
  ) => Response;
  staleTime?: number;
}) {
  // type Response = RouteDefinition extends RouteWithJsonResponse
  //   ? RouteDefinition["responses"][200]["content"]["application/json"]
  //   : never;

  // duplicate definition of *Params above. working around a TS bug where TS
  // would incorrectly think these were of type `never`
  type PathParamsInner = RouteDefinition extends RouteWithPathParams
    ? RouteDefinition["parameters"]["path"]
    : never;
  type QueryParamsInner = RouteDefinition extends RouteWithQueryParams
    ? Partial<RouteDefinition["parameters"]["query"]>
    : never;

  type CombinedParams =
    | (PathParamsInner extends never
        ? QueryParamsInner extends never
          ? Record<string, never>
          : { query?: QueryParamsInner }
        : QueryParamsInner extends never
        ? { path: PathParamsInner }
        : { path: PathParamsInner; query?: QueryParamsInner })
    | typeof NOOP;

  const config: FetcherConfigs[keyof FetcherConfigs] = {
    invalidator: shouldInvalidate,
    reducer: memberReducer,
  };

  const fetcherConfigs = combinedConfigsRef.current.fetchers;

  if (!fetcherConfigs[pathPattern]) {
    fetcherConfigs[pathPattern] = config;
  } else if (fetcherConfigs[pathPattern] !== config) {
    throw new Error(
      `Name collision when defining a new Fetcher: ${pathPattern}`
    );
  }

  function queryKey(params: CombinedParams) {
    const pathParams = (params as any)?.path as PathParams | undefined;
    const queryParams = (params as any)?.query as QueryParams | undefined;
    return [pathPattern, pathParams, queryParams];
  }

  function useFetcher(
    params: CombinedParams,
    options: UseQueryOptions<Response, Error> = { staleTime }
  ) {
    const pathParams = (params as any)?.path as PathParams | undefined;
    const queryParams = (params as any)?.query as QueryParams | undefined;

    return useQuery<Response, Error>(
      queryKey(params),
      async () => {
        if (params === NOOP) {
          return undefined as Response;
        }

        // template path params if supplied
        let templatedPath: string = pathPattern;
        if (pathParams) {
          templatedPath = Object.entries(
            pathParams as PathParams
          ).reduce<string>((memo, [key, value]) => {
            return memo.replace(`{${key}}`, value as string);
          }, pathPattern);
        }

        // fill in query params if supplied
        let qs = "";
        if (queryParams) {
          const searchParams = new URLSearchParams();
          Object.entries(queryParams).forEach(([key, value]) => {
            searchParams.set(key, String(value));
          });
          qs = "?" + searchParams.toString();
        }

        const url = `${templatedPath}${qs}`;

        const response = await getApiClient().get<Response>(url);
        return response.data;
      },
      options
    );
  }

  const ret: typeof useFetcher & {
    queryKey: typeof queryKey;
  } = useFetcher;
  useFetcher.queryKey = queryKey;

  return ret;
}

export function makeCollectionFetcher<
  Path extends keyof paths,
  Method extends "get",
  RouteDefinition extends Method extends keyof paths[Path]
    ? paths[Path][Method]
    : never,
  PathParams extends RouteDefinition extends RouteWithPathParams
    ? RouteDefinition["parameters"]["path"]
    : never,
  QueryParams extends RouteDefinition extends RouteWithQueryParams
    ? RouteDefinition["parameters"]["query"]
    : never,
  Items extends RouteDefinition extends RouteWithCollectionJsonResponse
    ? RouteDefinition["responses"][200]["content"]["application/json"]["_items"]
    : never
>({
  pathPattern,
  shouldInvalidate,
  collectionReducer,
  staleTime,
}: {
  pathPattern: Path;
  shouldInvalidate?: (
    fetcherParams: { path: PathParams; query: QueryParams },
    mutation: any
  ) => boolean;
  collectionReducer?: (
    fetcherParams: { path: PathParams; query: QueryParams },
    data: Items,
    mutation: any
  ) => Items;
  staleTime?: number;
}) {
  type Response = RouteDefinition extends RouteWithJsonResponse
    ? RouteDefinition["responses"][200]["content"]["application/json"]
    : never;

  // duplicate definition of *Params above. working around a TS bug where TS
  // would incorrectly think these were of type `never`
  type PathParamsInner = RouteDefinition extends RouteWithPathParams
    ? RouteDefinition["parameters"]["path"]
    : never;
  type QueryParamsInner = RouteDefinition extends RouteWithQueryParams
    ? RouteDefinition["parameters"]["query"]
    : never;

  type CombinedParams =
    | (PathParamsInner extends never
        ? QueryParamsInner extends never
          ? Record<string, never>
          : { query: Partial<QueryParamsInner> }
        : QueryParamsInner extends never
        ? { path: PathParamsInner }
        : { path: PathParamsInner; query: Partial<QueryParamsInner> })
    | typeof NOOP;

  const config: FetcherConfigs[keyof FetcherConfigs] = {
    invalidator: shouldInvalidate,
    reducer: collectionReducer
      ? (
          fetcherParams: { path: PathParams; query: QueryParams },
          data: InfiniteData<Response>,
          mutation: any
        ): InfiniteData<any> => {
          const input = data?.pages.flatMap(({ _items }) => _items) as Items;
          const output = collectionReducer(fetcherParams, input, mutation);

          if (output !== input) {
            // collapse all pages into one page
            return {
              pageParams: [undefined],
              pages: [
                {
                  _items: output,
                  _links: data.pages[data.pages.length - 1]._links,
                },
              ],
            };
          }
          return data;
        }
      : undefined,
  };

  const fetcherConfigs = combinedConfigsRef.current.fetchers;

  if (!fetcherConfigs[pathPattern]) {
    fetcherConfigs[pathPattern] = config;
  } else if (fetcherConfigs[pathPattern] !== config) {
    throw new Error(
      `Name collision when defining a new Fetcher: ${pathPattern}`
    );
  }

  function queryKey(params: CombinedParams) {
    const pathParams = (params as any)?.path as PathParams | undefined;
    const queryParams = (params as any)?.query as QueryParams | undefined;
    return [pathPattern, pathParams, queryParams];
  }

  function useFetcher(
    params: CombinedParams,
    options: UseInfiniteQueryOptions<Response, Error> = { staleTime }
  ) {
    const pathParams = (params as any)?.path as PathParams | undefined;
    const queryParams = (params as any)?.query as QueryParams | undefined;

    const infiniteQueryOptions = useMemo(
      () => ({
        ...options,
        getNextPageParam,
      }),
      [options]
    );

    const ret = useInfiniteQuery<Response, Error>(
      queryKey(params),
      async ({ pageParam }) => {
        if (params === NOOP) {
          return {} as Response;
        }

        let url: string;
        if (pageParam) {
          // using a provided next/prev page url
          url = getRelativePath(pageParam);
        } else {
          // template path params if supplied
          let templatedPath: string = pathPattern;
          if (pathParams) {
            templatedPath = Object.entries(
              pathParams as PathParams
            ).reduce<string>((memo, [key, value]) => {
              return memo.replace(`{${key}}`, value as string);
            }, pathPattern);
          }

          // fill in query params if supplied
          let qs = "";
          if (queryParams) {
            const searchParams = new URLSearchParams();
            Object.entries(queryParams).forEach(([key, value]) => {
              searchParams.set(key, String(value));
            });
            qs = "?" + searchParams.toString();
          }

          url = `${templatedPath}${qs}`;
        }

        const response = await getApiClient().get<Response>(url);
        return response.data;
      },
      infiniteQueryOptions
    );

    // react-query returns data in pages, but we want a single array containing
    // the data from all pages.
    const collatedData = useMemo(() => {
      return ret.data?.pages.flatMap(({ _items }) => _items) as
        | Items
        | undefined;
    }, [ret.data?.pages]);

    return {
      ...ret,
      data: collatedData,
    };
  }

  const ret: typeof useFetcher & {
    queryKey: typeof queryKey;
  } = useFetcher;
  useFetcher.queryKey = queryKey;

  return ret;
}

type MutationAction =
  | {
      type: "mutate";
      mutationKey: string;
      params: unknown;
    }
  | {
      type: "success";
      mutationKey: string;
      params: unknown;
      data: unknown;
    }
  | {
      type: "error";
      mutationKey: string;
      params: unknown;
      error: unknown;
    };

type MakeOperationResult<
  TData = unknown,
  TError = unknown,
  TVariables = unknown,
  TContext = unknown
> = ((
  runtimeOptions?: UseMutationOptions<TData, TError, TVariables, TContext>
) => UseMutationResult<TData, TError, TVariables, TContext>) & {
  isMutate(action: any): action is { params: TVariables };
  isSuccess(action: any): action is { params: TVariables; data: TData };
  isError(action: any): action is { params: TVariables; data: TError };
};

export function makeOperation<
  TData = unknown,
  TError = unknown,
  TVariables = unknown,
  TContext = unknown
>(
  options: Omit<
    UseMutationOptions<TData, TError, TVariables, TContext>,
    // we're not accepting these callbacks for now.
    // you can still trigger side effects when calling the `mutate()` function,
    // or when you call use*Operation()
    // https://react-query.tanstack.com/guides/mutations#mutation-side-effects
    "onMutate" | "onSuccess" | "onError" | "onSettled"
  > & {
    // make mutationKey required, instead of optional
    mutationKey: string;
  }
): MakeOperationResult<TData, TError, TVariables, TContext> {
  function isMutate(action: any): action is {
    params: TVariables;
  } {
    return (
      action?.type === "mutate" && action?.mutationKey === options.mutationKey
    );
  }
  function isSuccess(action: any): action is {
    params: TVariables;
    data: TData;
  } {
    return (
      action?.type === "success" && action?.mutationKey === options.mutationKey
    );
  }
  function isError(action: any): action is {
    params: TVariables;
    data: TError;
  } {
    return (
      action?.type === "error" && action?.mutationKey === options.mutationKey
    );
  }

  type FinalOptions = UseMutationOptions<TData, TError, TVariables, TContext>;

  let didRegisterMutationKey = false;

  const { fetchers: fetcherConfigs, operations: operationConfigs } =
    combinedConfigsRef.current;
  if (!didRegisterMutationKey) {
    if (operationConfigs[options.mutationKey]) {
      throw new Error(
        `Name collision when defining a new Operation: ${options.mutationKey}`
      );
    }

    operationConfigs[options.mutationKey] = didRegisterMutationKey = true;
  }

  function useOperation(runtimeOptions?: FinalOptions) {
    const queryClient = useQueryClient();
    const runtimeOnMutate = runtimeOptions?.onMutate;
    const runtimeOnSuccess = runtimeOptions?.onSuccess;
    const runtimeOnError = runtimeOptions?.onError;

    const onMutate: FinalOptions["onMutate"] = useCallback(
      (variables) => {
        const action: MutationAction = {
          type: "mutate",
          mutationKey: options.mutationKey,
          params: variables,
        };

        triggerReducers(fetcherConfigs, queryClient, action);
        return runtimeOnMutate?.(variables);
      },
      [queryClient, runtimeOnMutate]
    );

    const onSuccess: FinalOptions["onSuccess"] = useCallback(
      (data, variables, context) => {
        const action: MutationAction = {
          type: "success",
          mutationKey: options.mutationKey,
          params: variables,
          data: data,
        };

        triggerReducers(fetcherConfigs, queryClient, action);
        triggerInvalidations(fetcherConfigs, queryClient, action);
        runtimeOnSuccess?.(data, variables, context);
      },
      [queryClient, runtimeOnSuccess]
    );

    const onError: FinalOptions["onError"] = useCallback(
      (error, variables, context) => {
        const action: MutationAction = {
          type: "error",
          mutationKey: options.mutationKey,
          params: variables,
          error,
        };

        triggerReducers(fetcherConfigs, queryClient, action);
        triggerInvalidations(fetcherConfigs, queryClient, action);
        runtimeOnError?.(error, variables, context);
      },
      [queryClient, runtimeOnError]
    );

    const finalOptions: FinalOptions = useMemo(
      () => ({
        ...options,
        onMutate,
        onSuccess,
        onError,
      }),
      [onError, onMutate, onSuccess]
    );

    return useMutation(finalOptions);
  }

  const ret: MakeOperationResult<TData, TError, TVariables, TContext> =
    useOperation;
  useOperation.isMutate = isMutate;
  useOperation.isSuccess = isSuccess;
  useOperation.isError = isError;

  return ret;
}

function triggerInvalidations(
  fetcherConfig: FetcherConfigs,
  queryClient: QueryClient,
  action: MutationAction
) {
  queryClient.invalidateQueries({
    predicate: (query) => {
      const fetcherName = query.queryKey[0] as string;
      const invalidator = fetcherConfig[fetcherName]?.invalidator;
      if (invalidator) {
        return invalidator(
          {
            path: query.queryKey[1],
            query: query.queryKey[2],
          },
          action
        );
      }
      return false;
    },
  });
}

function triggerReducers(
  fetcherConfig: FetcherConfigs,
  queryClient: QueryClient,
  action: MutationAction
) {
  queryClient
    .getQueryCache()
    .findAll()
    .forEach((query) => {
      const fetcherName = query.queryKey[0] as string;

      const reducer = fetcherConfig[fetcherName]?.reducer;
      if (!reducer) return;

      const data = queryClient.getQueryData(query.queryKey);
      if (!data) return;

      const out = reducer(
        {
          path: query.queryKey[1],
          query: query.queryKey[2],
        },
        data,
        action
      );

      if (out !== data) {
        queryClient.setQueryData(query.queryKey, out);
      }
    });
}

type HasRequestBody<T> = {
  requestBody: {
    content: {
      "application/json": T;
    };
  };
};

type HasResponseData<T> = {
  responses: {
    200: T;
  };
};

type HasJsonResponseData<T> = HasResponseData<{
  content: {
    "application/json": T;
  };
}>;

type Params<PathParameters, QueryParameters> = {} extends PathParameters
  ? {} extends QueryParameters
    ? {}
    : { query: QueryParameters }
  : {} extends QueryParameters
  ? { path: PathParameters }
  : { path: PathParameters; query: QueryParameters };

type MakeApiOperationVariables<PathParameters, QueryParameters, RequestBody> =
  (RequestBody extends void ? {} : { body: RequestBody }) &
    Params<PathParameters, QueryParameters>;

type PathPart<PathParameters> =
  | { interpolated: true; param: keyof PathParameters }
  | { interpolated: false; literal: string };

// Parse a given path to find which parts are literal and which parts are interpolated,
// returning a function that can parse an object of variables with them combined and return
// a separate url and request body.

// e.g., if the provided path is "/api/parent/{parentId}/student"
// then we'll return a function that accepts { foo: 5, parentId: "abc" }
// and returns { url: "/api/parent/abc/student", body: { foo: 5 } }
function parsePath<PathParameters, QueryParameters>(path: string) {
  // Split the path by /
  // Keep a record of whether each pathPart represents a literal string or some
  // value that should be interpolated
  const pathParts: PathPart<PathParameters>[] = path.split("/").map((part) => {
    const match = part.match(/{(.+)}/);
    if (match) {
      return { interpolated: true, param: match[1] as any };
    } else {
      return { interpolated: false, literal: part };
    }
  });

  // Return a function that parses the provided variables, distinguishing between
  // those that are path parameters that form the url and putting the rest in the body.
  return (params: Params<PathParameters, QueryParameters>): string => {
    const url = pathParts
      .map((part) =>
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        part.interpolated ? (params as any).path[part.param!] : part.literal
      )
      .join("/");

    if ("query" in params) {
      const queryParts = [];
      for (const [key, value] of Object.entries(params.query as any)) {
        if (value != null) {
          queryParts.push(`${key}=${encodeURIComponent(value as any)}`);
        }
      }
      const query = queryParts.join("&");
      return `${url}?${query}`;
    } else {
      return url;
    }
  };
}

// Handles a common use case where a mutation represents a single
// API request that sends/receives JSON. From the path and method
// declared as arguments, the type definitions should be able to
// look up the corresponding PathParameters and RequestBody, which
// form the TVariables, and the ResponseData, which becomes the TData.

export function makeApiOperation<
  Path extends keyof paths,
  Method extends keyof paths[Path],
  PathParameters = paths[Path][Method] extends { parameters: { path: any } }
    ? paths[Path][Method]["parameters"]["path"]
    : {},
  QueryParameters = paths[Path][Method] extends { parameters: { query: any } }
    ? paths[Path][Method]["parameters"]["query"]
    : {},
  RequestBody = paths[Path][Method] extends HasRequestBody<any>
    ? paths[Path][Method]["requestBody"]["content"]["application/json"]
    : void,
  ResponseData = paths[Path][Method] extends HasJsonResponseData<any>
    ? paths[Path][Method]["responses"][200]["content"]["application/json"]
    : void
>({
  path,
  method,
  useErrorBoundary,
}: paths[Path][Method] extends HasResponseData<any>
  ? {
      path: Path;
      method: Method;
      useErrorBoundary?: UseMutationOptions<
        ResponseData,
        Error,
        MakeApiOperationVariables<PathParameters, QueryParameters, RequestBody>
      >["useErrorBoundary"];
    }
  : never): MakeOperationResult<
  ResponseData,
  Error,
  MakeApiOperationVariables<PathParameters, QueryParameters, RequestBody>
> {
  const mutationKey = `${String(method)}:${path}`;

  const parseVariables = parsePath<PathParameters, QueryParameters>(path);

  return makeOperation<
    ResponseData,
    Error,
    MakeApiOperationVariables<PathParameters, QueryParameters, RequestBody>
  >({
    mutationKey,
    async mutationFn(
      variables: MakeApiOperationVariables<
        PathParameters,
        QueryParameters,
        RequestBody
      >
    ) {
      const url = parseVariables(variables);

      const response = await getApiClient().request({
        url,
        method: method as any,
        data: (variables as any).body,
        headers: { "Content-Type": "application/json" },
      });
      return response.data;
    },
    useErrorBoundary,
  });
}
